import React, {Suspense, lazy} from 'react';
import styled from 'styled-components';
import Skeleton from '@material-ui/lab/Skeleton';
import SGBody from 'components/Typography/SGBody';
import SGSubtitle from 'components/Typography/SGSubtitle';
import TunnelStatusIndicator from 'features/POSRegisters/POSTable/NetworkCircuit/components/TunnelStatusIndicator';
import TunnelStatus from 'features/POSRegisters/interfaces/TunnelStatus';
import Alert from 'features/POSRegisters/interfaces/AlertInfo';
import {withAlerts} from 'features/POSRegisters/POSTable/Registers/components/TableWithAlerts';
import type {NetworkCircuitScanDetails} from 'features/POSRegisters/interfaces/ScanDetails';

//#region styles
const TableWithAlerts = lazy(
  () => import('features/POSRegisters/POSTable/Registers/components/TableWithAlerts')
);

const StyledTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .MuiTableContainer-root {
    min-height: auto;
  }

  .MuiTableBody-root .MuiTableCell-body {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .MuiTableBody-root tbody td {
  }

  .end-table-spacer {
    border-bottom: 1px solid ${props => props.theme.grey};
    height: auto;
  }

  &:last-child .end-table-spacer {
    border-color: transparent;
  }
`;

const StyledCustomCell = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCustomCellHeader = styled(SGBody)`
  font-weight: 700;
`;

const StyledSkeleton = styled(Skeleton)`
  height: 300px;
  background-color: ${props => props.theme.text}0D;
`;

//#endregion styles

const statusText: Record<string, string> = {
  [TunnelStatus.STABLE]: 'stable',
  [TunnelStatus.DISCONNECTED]: 'disconnected',
  [TunnelStatus.STANDBY]: 'standby',
  [TunnelStatus.UNKNOWN]: '',
};

const columns = [
  {
    id: 'interface',
    label: 'Interface',
    width: '4.5rem',
    format: (value: string) => {
      return (
        <StyledCustomCell>
          <StyledCustomCellHeader variant={'b4'}>{value}</StyledCustomCellHeader>
        </StyledCustomCell>
      );
    },
  },
  {
    id: 'localExchangeCarrier',
    label: 'Local Exchange Carrier',
    width: '12rem',
  },
  {
    id: 'ipV4Address',
    label: 'IP Address',
    width: '8rem',
  },
  {
    id: 'tunnelStatus',
    label: 'Tunnel Status',
    width: '10rem',
    format: (value: string) => {
      return (
        <StyledCustomCell>
          <TunnelStatusIndicator status={value?.toLowerCase()} />
          <SGBody variant={'b4'}>{value ?? ''}</SGBody>
        </StyledCustomCell>
      );
    },
  },
  {
    id: 'uploadSpeedBandwidth',
    label: 'Upload Speed',
    width: '130px',
    format: (value: any) => (
      <StyledCustomCell>
        <SGSubtitle className="s2">0 / {value ?? ''}</SGSubtitle>
      </StyledCustomCell>
    ),
  },
  {
    id: 'downloadSpeedBandwidth',
    label: 'Download Speed',
    width: '140px',
    format: (value: any) => (
      <StyledCustomCell>
        <SGSubtitle className="s2">0 / {value ?? ''}</SGSubtitle>
      </StyledCustomCell>
    ),
  },
];

interface CircuitTableProps {
  scanResults: Partial<NetworkCircuitScanDetails>;
  alerts: Alert[];
}

function CircuitTable({scanResults, alerts}: CircuitTableProps) {
  return (
    <StyledTableContainer>
      <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
        <TableWithAlerts
          columns={columns}
          data={withAlerts([{scanResults, alerts}])}
          noDataText={'No circuits were found.'}
          loaded={true}
          disablePagination={true}
        />
      </Suspense>
    </StyledTableContainer>
  );
}

export default CircuitTable;
